import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue'

// Breadcrumbs that give way to the space they're in: when the whole trail doesn't fit, the crumbs
// on the left go first, one by one (each with the separator after it); the current page is never
// hidden — once it's the only one left and still doesn't fit, it gets cut with an ellipsis (that
// part is plain CSS: the page's item is the list's only shrinkable child, see BreadcrumbList.vue).
//
// The catch is measuring the space: the list sits in pills that hug their content (`w-fit`), so its
// own width is the content's width, never the room it could have. For the measurement the list is
// briefly made absurdly wide — every container up to the one that is really constrained shrinks it
// (`min-w-0` along the way), and its parent then reports the room there is. All of it within one
// task: set, read, restore, no frame ever paints it. The trail's natural width counts the page at
// its full text width (`scrollWidth` of the clipped element), so neither a running entrance
// animation nor the page's own truncation fools it into hiding or showing crumbs.

const COLLAPSED = 'data-collapsed'
// Ancestors watched for size changes: the pill around the list only resizes when the space it's
// in does, and that space is a few levels up (header → pill → content row → slot → nav).
const WATCHED_ANCESTORS = 8

function isSeparator(el: Element) {
  return el.getAttribute('data-slot') === 'breadcrumb-separator'
}

// A crumb's text as read: the page's `aria-label` (AnimatedBreadcrumbPage's letters are aria-hidden
// spans), otherwise its own text.
function crumbLabel(el: Element) {
  const page = el.querySelector('[aria-current="page"]')
  return (page?.getAttribute('aria-label') ?? el.textContent ?? '').replace(/\s+/g, ' ').trim()
}

// An item's width with its text uncut. A page with a single run inside (AnimatedBreadcrumbPage's letters)
// is measured by that run's box rather than `scrollWidth`: the letters' entrance scales them, and
// scrollable overflow counts transforms — a few px of wobble that would toggle crumbs near the edge.
function naturalWidth(el: HTMLElement) {
  let width = el.getBoundingClientRect().width
  for (const clipped of el.querySelectorAll<HTMLElement>('[data-slot="breadcrumb-page"], [data-slot="breadcrumb-link"]')) {
    const run = clipped.childElementCount === 1 ? clipped.firstElementChild : null
    const content = run ? run.getBoundingClientRect().width : clipped.scrollWidth
    width += Math.max(0, content - clipped.clientWidth)
  }
  return width
}

export function useCollapsingBreadcrumb(listRef: Ref<HTMLElement | null>) {
  /** Something is hidden or cut — the whole trail is then worth a tooltip. */
  const collapsed = ref(false)
  /** The whole trail as text, «Доска › Задача». */
  const trail = ref('')
  let frame = 0
  let resizeObserver: ResizeObserver | null = null
  let mutationObserver: MutationObserver | null = null

  function layout() {
    frame = 0
    const list = listRef.value
    const parent = list?.parentElement
    if (!list || !parent) return

    const children = Array.from(list.children) as HTMLElement[]
    for (const el of children) el.removeAttribute(COLLAPSED)

    const pageIndex = children.findLastIndex((el) => el.querySelector('[aria-current="page"]'))
    const keepFrom = pageIndex >= 0 ? pageIndex : children.length - 1
    trail.value = children
      .filter((el) => !isSeparator(el) && el.getClientRects().length > 0)
      .map(crumbLabel)
      .filter(Boolean)
      .join(' › ')

    const width = list.style.width
    list.style.width = '100000px'
    const available = parent.getBoundingClientRect().width
    list.style.width = width

    const gap = parseFloat(getComputedStyle(list).columnGap) || 0
    const rendered = children.map((el) => el.getClientRects().length > 0)
    const widths = children.map((el, i) => (rendered[i] ? naturalWidth(el) : 0))
    let total = widths.reduce((sum, w, i) => sum + (rendered[i] ? w + gap : 0), -gap)

    let i = 0
    while (total > available + 0.5 && i < keepFrom) {
      do {
        children[i]!.setAttribute(COLLAPSED, '')
        if (rendered[i]) total -= widths[i]! + gap
        i++
      } while (i < keepFrom && isSeparator(children[i]!))
    }
    // Crumbs hidden, or the page still doesn't fit on its own and its text is cut.
    collapsed.value = i > 0 || total > available + 0.5
  }

  function schedule() {
    if (!frame) frame = requestAnimationFrame(layout)
  }

  onMounted(() => {
    const list = listRef.value
    if (!list) return
    resizeObserver = new ResizeObserver(schedule)
    let el: HTMLElement | null = list
    for (let depth = 0; el && depth <= WATCHED_ANCESTORS; depth++, el = el.parentElement) resizeObserver.observe(el)
    mutationObserver = new MutationObserver(schedule)
    mutationObserver.observe(list, { childList: true, subtree: true, characterData: true })
    schedule()
  })

  onBeforeUnmount(() => {
    if (frame) cancelAnimationFrame(frame)
    resizeObserver?.disconnect()
    mutationObserver?.disconnect()
  })

  return { collapsed, trail }
}
